// src/features/billing/MiPlanPage.tsx
// ============================================================================
// MI PLAN (fase C, 18-ago-2026) — el estudio ve su suscripción y la paga sin
// pedírselo a nadie. Port de la pantalla de la agenda dental con los tokens de
// diseño de este front. Reglas que se respetan:
//  · El MONTO lo manda el servidor (precio acordado si lo hay). Aquí NO se calcula nada.
//  · Los beneficios vienen en plan_features (por vertical, fuente única en el backend).
//  · Una CORTESÍA (is_trial) no ve un cobro pendiente: ve que su plan está de regalo.
//  · Pagar por Mercado Pago NO activa el plan aquí — lo cierra el webhook al confirmar.
// ============================================================================
import { useEffect, useState } from 'react';
import {
  CreditCard, Check, Clock, AlertCircle, Building2, ExternalLink, Upload, Gift, HelpCircle,
  CalendarDays, MessageSquare, FileText, Wallet, Receipt, Sparkles, Headset,
  Users, GraduationCap, BarChart3, Stethoscope, RefreshCw,
} from 'lucide-react';
import {
  getMyPlan, getPaymentMethods, startMercadoPago, registerTransfer, uploadReceipt,
  startAutopay, cancelAutopay,
  periodoTexto, money,
  type PlanSubscription, type PlanPayment, type PaymentMethods, type PlanOption,
} from '../../services/subscriptionApi';

// Respuestas cortas y ciertas: lo que dicen ya lo cumple el backend (vigencia que se
// suma, modo consulta, tiempos de activación). Redactadas para el estudio.
const FAQS: { q: string; a: string }[] = [
  {
    q: '¿Qué pasa si mi plan se vence?',
    a: 'No pierdes nada: tu información queda intacta y puedes seguir consultándola. Solo se pausa el registro de alumnas, asistencias y pagos nuevos hasta que renueves.',
  },
  {
    q: '¿Puedo pagar antes de que venza?',
    a: 'Sí. El pago se suma a tu vigencia actual: no pierdes ni un día por adelantarte.',
  },
  {
    q: '¿Tarjeta o transferencia?',
    a: 'Con tarjeta tu plan se activa al instante. Con transferencia, sube tu comprobante y lo activamos en máximo 24 horas hábiles.',
  },
  {
    q: '¿Necesitas factura o tienes dudas de tu plan?',
    a: 'Escríbenos por WhatsApp — el mismo canal de soporte de siempre — y lo resolvemos.',
  },
];

const fecha = (d: string | null): string => {
  if (!d) return '—';
  try {
    return new Date(d).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return String(d); }
};

// Nombre genérico de ícono (lo manda el servidor con cada beneficio) → ícono real.
const FEATURE_ICONS: Record<string, typeof Check> = {
  calendar: CalendarDays, message: MessageSquare, stethoscope: Stethoscope, file: FileText,
  wallet: Wallet, receipt: Receipt, sparkles: Sparkles, headset: Headset,
  users: Users, graduation: GraduationCap, chart: BarChart3,
};

export default function MiPlanPage() {
  const [sub, setSub] = useState<PlanSubscription | null>(null);
  const [pagos, setPagos] = useState<PlanPayment[]>([]);
  const [metodos, setMetodos] = useState<PaymentMethods | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [yendoAPagar, setYendoAPagar] = useState(false);
  const [autopayBusy, setAutopayBusy] = useState(false);
  // Periodo ELEGIDO para pagar (27-sep-2026): mensual / semestral / anual. null = el vigente.
  const [planElegido, setPlanElegido] = useState<string | null>(null);
  const [subiendoComprobante, setSubiendoComprobante] = useState(false);
  const [comprobanteListo, setComprobanteListo] = useState(false);
  // ¿Venimos de regreso de Mercado Pago? (back_urls ?paid=1 / ?payfail=1). Se lee una vez
  // y se limpia de la URL para que un refresh no re-anuncie un pago viejo.
  const [retornoPago, setRetornoPago] = useState<'paid' | 'fail' | null>(null);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    const r = q.has('paid') ? 'paid' : q.has('payfail') ? 'fail' : null;
    if (!r) return;
    setRetornoPago(r);
    q.delete('paid'); q.delete('payfail');
    const rest = q.toString();
    window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
  }, []);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const [plan, mets] = await Promise.all([getMyPlan(), getPaymentMethods().catch(() => null)]);
        if (!vivo) return;
        setSub(plan.subscription);
        setPagos(plan.payments || []);
        if (mets) setMetodos(mets);
        // Preselección: su plan vigente si se puede; en gratis, el anual; si no, el primero permitido.
        const ops = (plan.subscription.plan_options || []).filter((o) => o.allowed);
        const pick = ops.find((o) => o.current) || ops.find((o) => o.code === 'pro_yearly') || ops[0];
        if (pick) setPlanElegido(pick.code);
      } catch (e) {
        if (vivo) setError(e instanceof Error ? e.message : 'No se pudo cargar tu plan');
      } finally {
        if (vivo) setCargando(false);
      }
    })();
    return () => { vivo = false; };
  }, []);

  const recargarPagos = async () => {
    try {
      const plan = await getMyPlan();
      setSub(plan.subscription);
      setPagos(plan.payments || []);
    } catch { /* la lista se refresca al volver a entrar */ }
  };

  // De regreso de MP con pago hecho: el webhook suele confirmar en segundos → refrescar
  // unas veces para que vea su plan ya activo. Se rinde a los ~15 s.
  useEffect(() => {
    if (retornoPago !== 'paid') return;
    let intentos = 0;
    const timer = setInterval(() => {
      intentos += 1;
      void recargarPagos();
      if (intentos >= 5) clearInterval(timer);
    }, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retornoPago]);

  const subirComprobante = async (file: File | null) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('El comprobante no puede pesar más de 5 MB'); return; }
    setSubiendoComprobante(true);
    setError(null);
    try {
      const url = await uploadReceipt(file);
      await registerTransfer(url, planElegido);
      setComprobanteListo(true);
      await recargarPagos();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo registrar tu transferencia');
    } finally {
      setSubiendoComprobante(false);
    }
  };

  const pagar = async () => {
    setYendoAPagar(true);
    setError(null);
    try {
      const { checkoutUrl } = await startMercadoPago(planElegido);
      window.location.href = checkoutUrl;   // se sale a Mercado Pago; vuelve al terminar
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo iniciar el pago');
      setYendoAPagar(false);
    }
  };

  if (cargando) {
    return (
      <div className="p-4">
        <h2 className="flex items-center gap-2 font-display text-2xl font-bold text-content"><CreditCard className="text-brand-blue" size={24} /> Mi plan</h2>
        <p className="text-sm text-content-3">Cargando…</p>
      </div>
    );
  }

  const esGratis = !sub || sub.plan_code === 'free';
  const esCortesia = !!sub?.is_trial;
  const sinMonto = !esGratis && !(Number(sub?.price) > 0);
  // Opciones de periodo del servidor y la elegida. En GRATIS esto es lo que permite contratar
  // desde aquí (antes «Sin plan activo» escondía todo lo de cobro).
  const opciones: PlanOption[] = sub?.plan_options || [];
  const opcion = opciones.find((o) => o.code === planElegido) || null;
  const montoPago = opcion ? opcion.amount : Number(sub?.price || 0);
  const mesesPago = opcion ? opcion.months : Number(sub?.plan_months || 12);
  const puedePagar = esGratis ? !!opcion : !sinMonto;
  const motivoOpcion = (o: PlanOption) => (o.reason === 'PLAN_LOCKED' ? 'Precio acordado: para cambiar de periodo escríbenos'
    : o.reason === 'AT_RENEWAL' ? 'Disponible cuando falten 30 días o menos para renovar' : '');
  const vencido = !!sub?.expired;
  // Candado de domiciliación (23-oct-2026, espejo de mercadopago.autopayPlanCheck): con plan
  // VIGENTE solo se domicilia ese mismo periodo; otro periodo se paga primero una vez.
  const planVigente = !!sub && sub.status === 'active' && sub.plan_code !== 'free'
    && !!sub.expires_at && new Date(sub.expires_at).getTime() > Date.now();
  const autopayOtroPeriodo = planVigente && !!planElegido && planElegido !== sub?.plan_code;
  const dias = sub?.days_left ?? null;
  const pendiente = pagos.find((p) => p.status === 'pending');
  const pagosVisibles = pagos.filter((p) => p.status === 'completed' || p.status === 'pending');
  const puedeMercadoPago = !!metodos?.available.includes('mercadopago');
  const banco = metodos?.bank_account || null;

  return (
    <div className="space-y-5 p-4">
      <div>
        <h2 className="mb-1 flex items-center gap-2 font-display text-2xl font-bold text-content"><CreditCard className="text-brand-blue" size={24} /> Mi plan</h2>
        <p className="text-sm text-content-3">Tu suscripción, lo que incluye y cómo renovarla</p>
      </div>

      {error && (
        <div className="rounded-xl border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800 dark:border-red-500/40 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}

      {/* ── De regreso de Mercado Pago ───────────────────────────────────── */}
      {retornoPago === 'paid' && (
        pendiente || !sub?.is_pro ? (
          <div className="flex items-start gap-2 rounded-xl border border-blue-300 bg-blue-50 px-4 py-3 text-sm text-blue-900 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-200">
            <Clock className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Recibimos tu pago. Tu plan se renueva en cuanto Mercado Pago lo confirma — suele tardar unos segundos.</span>
          </div>
        ) : (
          <div className="flex items-start gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200">
            <Check className="mt-0.5 h-4 w-4 shrink-0" />
            <span>¡Listo! Tu pago se acreditó y tu plan está activo.</span>
          </div>
        )
      )}
      {retornoPago === 'fail' && (
        <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>El pago no se completó y no se hizo ningún cargo. Puedes intentarlo de nuevo cuando quieras.</span>
        </div>
      )}

      {/* ── Estado del plan ──────────────────────────────────────────────── */}
      <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-content-3">Tu plan</p>
            <p className="mt-1 font-display text-2xl font-bold text-content">
              {esGratis ? 'Sin plan activo' : (sub?.plan_months === 1 ? 'Plan mensual' : sub?.plan_months === 6 ? 'Plan semestral' : 'Plan anual')}
            </p>
            {esGratis && opciones.length > 0 && (
              <p className="mt-1 text-sm text-content-3">
                Contrata aquí abajo: desde {money(Math.min(...opciones.map((o) => o.amount)), opciones[0].currency)} al mes.
              </p>
            )}
            {!esGratis && (
              <p className="mt-1 text-content-3">
                <span className="text-lg font-semibold text-content">{money(sub!.price, sub!.currency)}</span>{' '}
                {periodoTexto(sub!.plan_months)}
                {esCortesia && (
                  <span className="ml-2 rounded-md bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-500/15 dark:text-emerald-300">
                    de cortesía
                  </span>
                )}
              </p>
            )}
          </div>

          <div className="text-right">
            {sub?.expires_at ? (
              <>
                <p className="text-[11px] uppercase tracking-wide text-content-3">
                  {vencido ? 'Venció el' : 'Vigente hasta'}
                </p>
                <p className={`text-sm font-semibold ${vencido ? 'text-red-600 dark:text-red-400' : 'text-content'}`}>
                  {fecha(sub.expires_at)}
                </p>
                {!vencido && dias != null && (
                  <p className="text-xs text-content-3">{dias === 1 ? 'queda 1 día' : `quedan ${dias} días`}</p>
                )}
              </>
            ) : (
              !esGratis && (
                <>
                  <p className="text-[11px] uppercase tracking-wide text-content-3">Vigencia</p>
                  <p className="text-sm font-semibold text-content">Sin vencimiento</p>
                </>
              )
            )}
          </div>
        </div>

        {vencido && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Tu plan venció. Puedes seguir consultando toda tu información; para volver a
              registrar alumnas, asistencias y pagos, renuévalo aquí abajo.
            </span>
          </div>
        )}
        {!vencido && esCortesia && sub?.expires_at && (
          <div className="mt-4 flex items-start gap-2 rounded-xl border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-900 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-200">
            <Check className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Tu plan está activo como cortesía hasta el {fecha(sub.expires_at)}.
              {Number(sub.price) > 0 && (
                <> A partir de esa fecha se cobra {money(sub.price, sub.currency)} {periodoTexto(sub.plan_months)}.</>
              )}
            </span>
          </div>
        )}
      </div>

      {/* ── Cortesía sin monto: nada que pagar ───────────────────────────── */}
      {sinMonto && (
        <div className="rounded-2xl border border-emerald-300 bg-emerald-50 p-4 dark:border-emerald-500/40 dark:bg-emerald-500/10">
          <div className="flex items-start gap-3">
            <Gift className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
            <p className="text-sm text-emerald-900 dark:text-emerald-200">
              <span className="font-semibold">Tu plan es una cortesía.</span> No tienes nada que
              pagar: todas tus funciones están activas.
            </p>
          </div>
        </div>
      )}

      {/* ── Qué incluye el plan (por vertical, lo manda el servidor). Con la versión rica
          (ícono+título+desc, copy de la landing) se pinta como tarjetas; sin ella, checklist. */}
      {!esGratis && ((sub?.plan_features_rich?.length ?? 0) > 0 || (sub?.plan_features?.length ?? 0) > 0) && (
        <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
          <h3 className="font-display text-base font-semibold text-content">Qué incluye tu plan</h3>
          {(sub?.plan_features_rich?.length ?? 0) > 0 ? (
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {sub!.plan_features_rich!.map((f) => {
                const Icon = FEATURE_ICONS[f.icon] || Check;
                return (
                  <li key={f.title} className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300">
                      <Icon size={18} />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-semibold text-content">{f.title}</span>
                      <span className="mt-0.5 block text-xs leading-relaxed text-content-3">{f.desc}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <ul className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
              {sub!.plan_features!.map((f) => (
                <li key={f} className="flex items-start gap-2 text-sm text-content">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-400" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* ── Pago pendiente (si lo hay) ───────────────────────────────────── */}
      {pendiente && (
        <div className="rounded-2xl border border-blue-300 bg-blue-50 p-4 dark:border-blue-500/40 dark:bg-blue-500/10">
          <div className="flex items-start gap-3">
            <Clock className="mt-0.5 h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-blue-900 dark:text-blue-200">Tienes un pago en proceso</p>
              <p className="mt-0.5 text-sm text-blue-800 dark:text-blue-300/90">
                {money(pendiente.amount, pendiente.currency)} por{' '}
                {pendiente.method === 'mercadopago' ? 'Mercado Pago' : 'transferencia'}. En cuanto
                se confirme, tu plan se renueva solo.
              </p>
            </div>
            {pendiente.checkout_url && (
              <a
                href={pendiente.checkout_url}
                className="shrink-0 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-700"
              >
                Continuar
              </a>
            )}
          </div>
        </div>
      )}

      {/* ── Pago automático (domiciliación, 30-ago-2026): autoriza una vez, cobra solo.
          El primer cargo respeta la vigencia pagada; se apaga aquí mismo cuando quiera. */}
      {(esGratis ? !!opcion : (!sinMonto && !esCortesia)) && puedeMercadoPago && (
        <div className="rounded-xl border border-default bg-surface-elevated p-5">
          <h2 className="flex items-center gap-2 text-base font-semibold text-content">
            <RefreshCw className="h-4 w-4" /> Pago automático
            {sub?.autopay_status === 'authorized' && (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400">ACTIVO</span>
            )}
          </h2>
          {sub?.autopay_status === 'authorized' ? (
            <>
              <p className="mt-1 text-sm text-content-muted">
                {money(sub.price, sub.currency)} {periodoTexto(sub.plan_months)} se cobra solo a tu
                tarjeta vía Mercado Pago. Sin recordatorios ni pagos manuales.
              </p>
              <button type="button" disabled={autopayBusy}
                onClick={async () => {
                  if (!window.confirm('¿Apagar el pago automático? Tu plan vigente no se pierde; volverás a renovar manualmente.')) return;
                  setAutopayBusy(true);
                  try { await cancelAutopay(); await recargarPagos(); } catch (e) { setError(e instanceof Error ? e.message : 'No se pudo cancelar'); }
                  setAutopayBusy(false);
                }}
                className="mt-3 rounded-md border border-default px-4 py-2 text-sm font-semibold text-content-muted transition-colors hover:bg-surface disabled:opacity-60">
                Apagar pago automático
              </button>
            </>
          ) : (
            <>
              <p className="mt-1 text-sm text-content-muted">
                Domicilia tu pago y olvídate de renovar: autorizas tu tarjeta una sola vez y
                {' '}{money(montoPago, sub!.currency)} {periodoTexto(mesesPago)} se cobra solo.
                {esGratis && ' El primer cargo es hoy y tu plan queda activo al confirmarse.'}
                {!esGratis && sub?.expires_at && !vencido && ' El primer cargo será hasta tu próximo vencimiento — no pagas doble.'}
                {' '}Lo puedes apagar aquí cuando quieras.
              </p>
              {autopayOtroPeriodo ? (
                <p data-testid="autopay-otro-periodo" className="mt-3 rounded-md bg-surface-2 px-3 py-2 text-sm text-content-3">
                  Para domiciliar otro periodo, primero págalo una vez (tarjeta o transferencia); la domiciliación se activa con el plan que tengas vigente.
                </p>
              ) : (
              <button type="button" data-testid="btn-autopay-start" disabled={autopayBusy}
                onClick={async () => {
                  setAutopayBusy(true);
                  try { const r = await startAutopay(planElegido); window.location.href = r.checkoutUrl; }
                  catch (e) { setError(e instanceof Error ? e.message : 'No se pudo iniciar la domiciliación'); setAutopayBusy(false); }
                }}
                className="mt-3 flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60">
                <RefreshCw className="h-4 w-4" /> {autopayBusy ? 'Abriendo Mercado Pago…' : 'Activar pago automático'}
              </button>
              )}
              <p className="mt-2 text-xs text-content-muted">Necesitas una cuenta de Mercado Pago (se crea gratis al autorizar).</p>
            </>
          )}
        </div>
      )}

      {/* ── Cómo pagar: dos tarjetas GEMELAS ─────────────────────────────── */}
      {puedePagar && (
        <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
          <h3 className="font-display text-base font-semibold text-content">
            {esGratis ? 'Contratar mi plan' : vencido ? 'Renovar mi plan' : 'Pagar por adelantado'}
          </h3>
          <p className="mt-1 text-sm text-content-3">
            {money(montoPago, sub!.currency)} {periodoTexto(mesesPago)}
            {!esGratis && sub!.expires_at && !vencido && ' · se suma a tu vigencia actual, no la pierdes'}
          </p>

          {/* Periodo (27-sep-2026): mensual / semestral / anual. Montos y permisos del servidor:
              precio acordado = solo su plan; acortar periodo = solo a ≤30 días de renovar. */}
          {opciones.length > 1 && (
            <div className="mt-3 grid gap-2 sm:grid-cols-3">
              {opciones.map((o) => {
                const sel = o.code === planElegido;
                return (
                  <button key={o.code} type="button" disabled={!o.allowed} onClick={() => setPlanElegido(o.code)}
                    title={o.allowed ? '' : motivoOpcion(o)}
                    className={`rounded-xl border px-3 py-2 text-left transition-colors ${sel ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500 dark:bg-blue-500/10' : 'border-line hover:bg-surface-2'} ${o.allowed ? '' : 'cursor-not-allowed opacity-50'}`}>
                    <span className="block text-sm font-semibold text-content">{o.label}{o.current ? ' · tu plan actual' : ''}</span>
                    <span className="block text-lg font-bold text-content">{money(o.amount, o.currency)}</span>
                    <span className="block text-xs text-content-3">{periodoTexto(o.months)}</span>
                    {!o.allowed && <span className="mt-1 block text-[11px] text-content-3">{motivoOpcion(o)}</span>}
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {puedeMercadoPago && (
              <div className="flex flex-col rounded-xl border border-line p-4">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-content">
                  <CreditCard className="h-4 w-4" /> Tarjeta
                </p>
                <p className="mt-1 flex-1 text-xs text-content-3">
                  Débito o crédito, vía Mercado Pago. Tu plan se activa al instante en cuanto
                  se confirma el pago.
                </p>
                <button
                  type="button"
                  onClick={pagar}
                  disabled={yendoAPagar}
                  className="mt-3 flex items-center justify-center gap-2 rounded-lg bg-accent-solid px-4 py-2 text-sm font-semibold text-on-accent transition hover:bg-accent-solid/90 disabled:opacity-60"
                >
                  {yendoAPagar ? 'Abriendo Mercado Pago…' : 'Pagar con tarjeta'}
                  {!yendoAPagar && <ExternalLink className="h-3.5 w-3.5 opacity-80" />}
                </button>
              </div>
            )}

            {banco?.clabe && (
              <div className="flex flex-col rounded-xl border border-line p-4">
                <p className="flex items-center gap-1.5 text-sm font-semibold text-content">
                  <Building2 className="h-4 w-4" /> Transferencia
                </p>
                <p className="mt-1 text-xs text-content-3">
                  {banco.bank}
                  {banco.accountName ? ` · ${banco.accountName}` : ''} — sube tu comprobante y
                  te activamos en máximo 24 h hábiles.
                </p>
                <p className="mt-1 flex-1 select-all font-mono text-sm text-content">{banco.clabe}</p>
                {comprobanteListo ? (
                  <p className="mt-3 flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
                    <Check className="h-3.5 w-3.5" /> Comprobante recibido: tu pago quedó en revisión.
                  </p>
                ) : (
                  <label className={`mt-3 flex cursor-pointer items-center justify-center gap-1.5 rounded-lg border border-dashed border-line px-3 py-2 text-xs font-medium text-content transition hover:bg-surface-2 ${subiendoComprobante ? 'pointer-events-none opacity-60' : ''}`}>
                    <Upload className="h-3.5 w-3.5" />
                    {subiendoComprobante ? 'Subiendo…' : 'Subir comprobante (foto o captura)'}
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      disabled={subiendoComprobante}
                      onChange={(e) => { void subirComprobante(e.target.files?.[0] || null); e.target.value = ''; }}
                    />
                  </label>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Preguntas frecuentes ────────────────────────────────────────── */}
      <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
        <h3 className="flex items-center gap-2 font-display text-base font-semibold text-content">
          <HelpCircle className="h-4 w-4 text-content-3" /> Preguntas frecuentes
        </h3>
        <div className="mt-2 divide-y divide-line">
          {FAQS.map((f) => (
            <details key={f.q} className="group py-2">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-2 text-sm font-medium text-content [&::-webkit-details-marker]:hidden">
                {f.q}
                <span className="text-content-3 transition-transform group-open:rotate-90">›</span>
              </summary>
              <p className="mt-1.5 pr-6 text-sm text-content-3">{f.a}</p>
            </details>
          ))}
        </div>
      </div>

      {/* ── Historial ───────────────────────────────────────────────────── */}
      {pagosVisibles.length > 0 && (
        <div className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
          <h3 className="font-display text-base font-semibold text-content">Mis pagos</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-content-3">
                  <th className="pb-2 pr-3 font-medium">Fecha</th>
                  <th className="pb-2 pr-3 font-medium">Monto</th>
                  <th className="pb-2 pr-3 font-medium">Forma</th>
                  <th className="pb-2 font-medium">Estado</th>
                </tr>
              </thead>
              <tbody>
                {pagosVisibles.map((p) => (
                  <tr key={p.id} className="border-t border-line">
                    <td className="py-2 pr-3 text-content-3">{fecha(p.created_at)}</td>
                    <td className="py-2 pr-3 font-medium text-content">{money(p.amount, p.currency)}</td>
                    <td className="py-2 pr-3 text-content-3">
                      {p.method === 'mercadopago' ? 'Tarjeta' : p.method === 'btcpay' ? 'Cripto' : 'Transferencia'}
                    </td>
                    <td className="py-2">
                      {p.status === 'completed' ? (
                        <span className="text-emerald-700 dark:text-emerald-400">Pagado</span>
                      ) : p.status === 'pending' ? (
                        <span className="text-blue-700 dark:text-blue-400">En proceso</span>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
